import { createElement } from 'react'
import CommentIcon from '@mui/icons-material/Comment'
import ImageIcon from '@mui/icons-material/Image'
import TableChartIcon from '@mui/icons-material/TableChart'
import { ElementListTab } from './types'

/** Default preferred width of the element list pane (px) — shared by the inner Allotment and the
 *  outlet sizing in EditorSceneLayout so the drawer beside it keeps enough room. */
export const ELEMENT_LIST_PANE_DEFAULT_WIDTH = 360

export const ELEMENT_LIST_PANE_MIN_WIDTH = 240

/** Fixed row height — matches the compact ElementRow layout (label + section line). */
export const ELEMENT_LIST_ROW_HEIGHT = 52

/** Below this pane width, tabs fall back to their short labels. */
export const ELEMENT_LIST_TAB_LABEL_WIDTH_THRESHOLD = 400

/**
 * Shared primary/secondary tab set reused across pages (StructuralPlanning, GlobalLoads, …).
 * Each sub-tab is a single logical category — element types are never mixed within one list.
 */
export const DEFAULT_ELEMENT_LIST_TABS: ElementListTab[] = [
  {
    id: 'walls',
    label: 'Wand',
    shortLabel: 'W',
    subTabs: [
      {
        id: 'walls',
        label: 'Wand',
        shortLabel: 'W',
        elementTypes: ['outer_walls', 'inner_walls'],
      },
    ],
  },
  {
    id: 'rips',
    label: 'Rippe',
    shortLabel: 'WS',
    subTabs: [{ id: 'rips', label: 'Rippe', shortLabel: 'WS', elementTypes: ['rips'] }],
  },
  {
    id: 'lintels',
    label: 'Sturz',
    shortLabel: 'FS',
    subTabs: [{ id: 'lintels', label: 'Sturz', shortLabel: 'FS', elementTypes: ['lintels'] }],
  },
  {
    id: 'slabs',
    label: 'Decke',
    shortLabel: 'DE',
    subTabs: [
      {
        id: 'slab-positions',
        label: 'Deckenpositionen',
        shortLabel: 'DE-Pos',
        elementTypes: ['vertical_slabs'],
      },
      { id: 'slabs', label: 'Deckenfläche', shortLabel: 'DE-Fläche', elementTypes: ['slabs'] },
    ],
  },
  {
    id: 'roofs',
    label: 'Dach',
    shortLabel: 'DA',
    subTabs: [
      {
        id: 'roof-positions',
        label: 'Dachpositionen',
        shortLabel: 'DA-Pos',
        elementTypes: ['vertical_roof_slabs'],
      },
      { id: 'roof-slabs', label: 'Dächer', shortLabel: 'DA-Fläche', elementTypes: ['roof_slabs'] },
    ],
  },
  {
    id: 'beams',
    label: 'Unterzüge',
    shortLabel: 'UZ',
    subTabs: [{ id: 'beams', label: 'Unterzüge', shortLabel: 'UZ', elementTypes: ['beams'] }],
  },
  {
    id: 'purlins',
    label: 'Pfetten',
    shortLabel: 'P',
    subTabs: [{ id: 'purlins', label: 'Pfetten', shortLabel: 'P', elementTypes: ['purlins'] }],
  },
  {
    id: 'columns',
    label: 'Stützen',
    shortLabel: 'C',
    subTabs: [{ id: 'columns', label: 'Stützen', shortLabel: 'C', elementTypes: ['columns'] }],
  },
  {
    id: 'foundation',
    label: 'Fundament',
    shortLabel: 'FU',
    subTabs: [
      { id: 'foundation', label: 'Fundament', shortLabel: 'FU', elementTypes: ['foundation'] },
    ],
  },
]

/** Reserved primary-tab id: when active the pane shows the project plan image cards list. */
export const IMAGE_CARDS_TAB_ID = 'image-cards'

/** Permanent "Plan-Bilder" tab appended after the element tabs. */
export const IMAGE_CARDS_ELEMENT_LIST_TAB: ElementListTab = {
  id: IMAGE_CARDS_TAB_ID,
  label: 'Plan-Bilder',
  shortLabel: 'BI',
  shortIcon: createElement(ImageIcon, { fontSize: 'small' }),
  subTabs: [{ id: IMAGE_CARDS_TAB_ID, label: 'Plan-Bilder', shortLabel: 'BI', elementTypes: [] }],
}

/** Reserved primary-tab id: when active the pane shows the project plan tables list. */
export const TABLES_TAB_ID = 'tables'

/** Permanent "Tabellen" tab appended after the element tabs. Carries no element types — the pane
 *  special-cases it to render the tables list pane. */
export const TABLES_ELEMENT_LIST_TAB: ElementListTab = {
  id: TABLES_TAB_ID,
  label: 'Tabellen',
  shortLabel: 'T',
  shortIcon: createElement(TableChartIcon, { fontSize: 'small' }),
  subTabs: [{ id: TABLES_TAB_ID, label: 'Tabellen', shortLabel: 'T', elementTypes: [] }],
}

/** Reserved primary-tab id: when active the pane shows the project comments list, not elements. */
export const COMMENTS_TAB_ID = 'comments'

/** Permanent "Kommentare" tab appended after the element tabs. Carries no element types — the pane
 *  special-cases it to render the comments list pane. */
export const COMMENTS_ELEMENT_LIST_TAB: ElementListTab = {
  id: COMMENTS_TAB_ID,
  label: 'Kommentare',
  shortLabel: 'K',
  shortIcon: createElement(CommentIcon, { fontSize: 'small' }),
  subTabs: [{ id: COMMENTS_TAB_ID, label: 'Kommentare', shortLabel: 'K', elementTypes: [] }],
}
